import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Drawer,
  Space,
  Typography,
  Image,
  Row,
  Col,
  Button,
  Tag,
  Descriptions,
  Divider,
} from "antd";
import PersonChip from "./PersonChip";
import {
  EnvironmentOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import histo from "../assets/histo.png";
import macro from "../assets/macro.jpg";
import radio from "../assets/radio.png";

const { Title, Paragraph, Text } = Typography;

const MODALITY_FALLBACK = { histo, macro, radio };

const CaseDrawer = ({ visible, onClose, selectedCase }) => {
  const navigate = useNavigate();

  const handleOpenViewer = () => {
    onClose?.();
    if (selectedCase?.key) {
      navigate(`/cas/${selectedCase.key}`);
    } else {
      navigate("/blank");
    }
  };

  const handleOpenCourse = (courseId) => {
    if (!courseId) return;
    onClose?.();
    navigate(`/cours/${courseId}`);
  };

  const courseLabel = selectedCase
    ? selectedCase.courseName || selectedCase.course
    : null;
  const courseIsKnown = Boolean(selectedCase?.course);

  return (
    <Drawer
      title="Détails du cas"
      placement="right"
      closable
      onClose={onClose}
      open={visible}
      width={480}
      footer={
        selectedCase ? (
          <Button
            type="primary"
            block
            size="large"
            icon={<PlayCircleOutlined />}
            onClick={handleOpenViewer}
          >
            Ouvrir la visualisation
          </Button>
        ) : null
      }
    >
      {selectedCase && (
        <>
          <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>
            {selectedCase.pathology}
          </Title>
          <Space wrap size={[4, 8]} style={{ marginBottom: 16 }}>
            <Tag icon={<EnvironmentOutlined />}>{selectedCase.region}</Tag>
          </Space>

          <Descriptions
            size="small"
            column={1}
            colon={false}
            labelStyle={{ width: 110, color: "var(--color-text-secondary)" }}
          >
            <Descriptions.Item label="Cours">
              {courseIsKnown ? (
                <Button
                  type="link"
                  style={{ padding: 0, height: "auto" }}
                  onClick={() => handleOpenCourse(selectedCase.course)}
                >
                  {courseLabel}
                </Button>
              ) : (
                <Text type="secondary">{courseLabel || "—"}</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Date">
              {selectedCase.date || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Propriétaire">
              <PersonChip name={selectedCase.owner} />
            </Descriptions.Item>
          </Descriptions>

          <Divider style={{ margin: "16px 0" }} />

          <Title level={5} style={{ marginBottom: 8 }}>
            Résumé clinique
          </Title>
          <Paragraph
            style={{ marginBottom: 0 }}
            type={selectedCase.clinicalSummary ? undefined : "secondary"}
          >
            {selectedCase.clinicalSummary || "Aucun résumé clinique fourni."}
          </Paragraph>

          <Divider style={{ margin: "16px 0" }} />

          <Title level={5} style={{ marginBottom: 12 }}>
            Imagerie
          </Title>
          {selectedCase.images && selectedCase.images.length > 0 ? (
            <Row gutter={[8, 8]}>
              {selectedCase.images.map((img) => (
                <Col span={12} key={img.id}>
                  <Image
                    src={img.thumbnailUrl || MODALITY_FALLBACK[img.modality]}
                    fallback={MODALITY_FALLBACK[img.modality]}
                    preview={false}
                    alt={img.name || img.modality}
                    width="100%"
                    style={{ borderRadius: 6, objectFit: "cover", aspectRatio: "4 / 3" }}
                  />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {img.name || img.modality}
                  </Text>
                </Col>
              ))}
            </Row>
          ) : (
            <Text type="secondary">Aucune imagerie associée à ce cas.</Text>
          )}
        </>
      )}
    </Drawer>
  );
};

export default CaseDrawer;
